import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { logError } from '../lib/errorLogger';
import { buildDefaultFlagMap, isFeatureEnabled } from '../lib/featureFlags';

export const POINTS_LOW_THRESHOLD = 100;

export function notifyPointsBalanceChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mingo-points-updated'));
  }
}

/** @param {unknown} err */
export function isInsufficientPointsRpcError(err) {
  const msg = err && typeof err === 'object' && 'message' in err ? String(err.message) : String(err || '');
  return msg.includes('insufficient_points') || msg.includes('P0001');
}

/**
 * Server-side safe deduction (row lock + ledger). Caller must be the same user as p_user_id.
 * @param {string} userId public.users.id
 * @param {number} amount positive number of points to subtract
 * @param {string} [description]
 * @returns {Promise<{ ok: boolean, balance?: number }>}
 */
export async function deductPointsRpc(userId, amount, description = '') {
  try {
    if (!isSupabaseConfigured) throw new Error('NO_SB');
    const sb = getSupabase();
    if (!sb || !userId) throw new Error('NO_USER');
    const { data, error } = await sb.rpc('deduct_points', {
      p_user_id: userId,
      p_amount: Math.floor(Number(amount)),
      p_service_type: 'general',
      p_description: description || '',
    });
    if (error) throw error;
    notifyPointsBalanceChanged();
    return data && typeof data === 'object' ? data : { ok: true };
  } catch (error) {
    void logError(error, {
      page: 'points',
      action: 'deduct_points',
      userId: userId || null,
    });
    throw error;
  }
}

/**
 * Atomic: deduct points + insert ad inquiry (status active) in one DB transaction.
 * @param {{ scope: string, businessName: string, contact: string, message: string, pointCost: number }} p
 */
export async function submitAdInquiryWithPointsRpc(p) {
  try {
    if (!isSupabaseConfigured) throw new Error('NO_SB');
    const sb = getSupabase();
    if (!sb) throw new Error('NO_SB');
    const { data, error } = await sb.rpc('submit_ad_inquiry_with_points', {
      p_scope: p.scope,
      p_business_name: p.businessName || '',
      p_contact: p.contact,
      p_message: p.message,
      p_point_cost: Math.floor(Number(p.pointCost)),
    });
    if (error) throw error;
    notifyPointsBalanceChanged();
    return data;
  } catch (error) {
    void logError(error, {
      page: 'ad-inquiry',
      action: 'submit_ad_inquiry_with_points',
    });
    throw error;
  }
}

/**
 * @param {string} userId public.users.id
 * @param {number} amount
 * @param {string} type
 * @param {string} [description]
 * @returns {Promise<boolean>}
 */
export async function insertUserPointsLedger(userId, amount, type, description = '') {
  if (!isSupabaseConfigured) return false;
  const sb = getSupabase();
  if (!sb || !userId) return false;
  const { error } = await sb.from('user_points').insert({
    user_id: userId,
    amount,
    type,
    description: description || null,
  });
  if (error && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn('[points]', error.message);
  }
  if (!error) notifyPointsBalanceChanged();
  return !error;
}

/** @param {string} userId */
export async function hasPointEvent(userId, type) {
  const sb = getSupabase();
  if (!sb || !userId) return false;
  const { data, error } = await sb.from('user_points').select('id').eq('user_id', userId).eq('type', type).limit(1);
  if (error) return false;
  return Array.isArray(data) && data.length > 0;
}

/**
 * Award once per user per type (idempotent).
 * @returns {Promise<boolean>} true if a new ledger row was inserted
 */
export async function awardPointsOnce(userId, type, amount, description) {
  if (!userId || !amount) return false;
  if (await hasPointEvent(userId, type)) return false;
  return insertUserPointsLedger(userId, amount, type, description);
}

/** @param {string} userId */
export async function fetchUserPointsHistory(userId, { limit = 40 } = {}) {
  const sb = getSupabase();
  if (!sb || !userId) return [];
  const { data, error } = await sb
    .from('user_points')
    .select('id, amount, type, description, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}

/** @returns {Promise<boolean>} */
export async function tryAwardDailyLogin(userId) {
  if (!userId || typeof window === 'undefined') return false;
  const key = `mingo_daily_pt_${userId}_${new Date().toISOString().slice(0, 10)}`;
  if (window.localStorage.getItem(key)) return false;
  const awarded = await awardPointsOnce(userId, 'daily_login', 10, '매일 접속');
  if (awarded) window.localStorage.setItem(key, '1');
  return awarded;
}

/**
 * 첫 번째 게시 콘텐츠(거래 글 또는 커뮤니티 글 합산 1건)일 때 1회 지급.
 * @returns {Promise<boolean>}
 */
export async function maybeAwardFirstUserContent(userId) {
  const sb = getSupabase();
  if (!sb || !userId) return false;
  const [postsRes, productsRes] = await Promise.all([
    sb.from('posts').select('id', { count: 'exact', head: true }).eq('author_user_id', userId),
    sb.from('products').select('id', { count: 'exact', head: true }).eq('author_user_id', userId),
  ]);
  const n = (postsRes.count ?? 0) + (productsRes.count ?? 0);
  if (n !== 1) return false;
  return awardPointsOnce(userId, 'first_post', 50, '첫 글 작성');
}

/** Signup welcome — call once after profile row exists. @returns {Promise<boolean>} */
export async function awardSignupWelcome(userId) {
  return awardPointsOnce(userId, 'signup', 100, '회원가입');
}

/**
 * Wallet / prepaid points (foundation — no Stripe yet).
 * 1 point = $0.01 USD · 100 points = $1 USD
 * Starter 500P=$5 · Basic 1100P=$10 · Pro 2500P=$20 · Premium 6500P=$50
 */
export const POINT_RATE = 100;

export const pointsToUSD = (points) => (Number(points) / POINT_RATE).toFixed(2);

export const usdToPoints = (usd) => Math.floor(Number(usd) * POINT_RATE);

function isPointsSystemEnabled(featureFlagMap) {
  return isFeatureEnabled(featureFlagMap || buildDefaultFlagMap(), 'points_system');
}

/**
 * Deduct wallet points (RPC). When `points_system` is OFF, no-op success (free period).
 * @param {Record<string, { is_enabled: boolean }>} [params.featureFlagMap]
 */
export async function deductPoints({
  userId,
  amount,
  serviceType,
  serviceRef = null,
  description = null,
  idempotencyKey = null,
  featureFlagMap = null,
}) {
  if (!isPointsSystemEnabled(featureFlagMap)) {
    return { success: true, skipped: true };
  }
  if (!isSupabaseConfigured) throw new Error('NO_SB');
  const sb = getSupabase();
  if (!sb || !userId) throw new Error('NO_USER');
  const { data, error } = await sb.rpc('deduct_points', {
    p_user_id: userId,
    p_amount: Math.floor(Number(amount)),
    p_service_type: serviceType,
    p_service_ref: serviceRef,
    p_description: description,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw error;
  notifyPointsBalanceChanged();
  return data;
}

/** @returns {Promise<{ points: number, points_hold: number, total_charged?: number, total_spent?: number, last_charged_at?: string | null }>} */
export async function getMyPoints(userId) {
  if (!isSupabaseConfigured || !userId) return { points: 0, points_hold: 0 };
  const sb = getSupabase();
  if (!sb) return { points: 0, points_hold: 0 };
  const { data, error } = await sb
    .from('wallets')
    .select('points, points_hold, total_charged, total_spent, last_charged_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error || !data) return { points: 0, points_hold: 0 };
  return data;
}

/** @returns {Promise<Array<Record<string, unknown>>>} */
export async function getPointHistory(userId) {
  if (!isSupabaseConfigured || !userId) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb
    .from('point_transactions')
    .select(
      'id, type, amount, points_before, points_after, source, description, service_type, created_at',
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) return [];
  return data ?? [];
}

/** @returns {Promise<Array<Record<string, unknown>>>} */
export async function getPointPackages() {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb
    .from('point_packages')
    .select('id, name, points, price_usd, bonus_points')
    .eq('is_active', true)
    .order('sort_order');
  if (error) return [];
  return data ?? [];
}
